import { Center, CircularProgress, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import {
  AbilityClaim,
  colors,
  formatDropdownValue,
  getClaimIsProcessing,
  getDateString,
  getDateTimeString,
  PregnancyClaim,
  version,
  WithId,
} from '@hb/shared'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useFormattedValue } from '@hb/react'
import { getClaim } from '../../../../backend/functions/claims'
import { CopyId } from '../../../CopyId'
import { AbilityClaimContent } from './Ability/AbilityClaim'
import { serviceTypeField } from './Ability/fields'
import { ClaimInfo } from './ClaimInfo'
import { status } from './fields'

const useFetchedClaim = (id: string, testMode: boolean) => {
  const [data, setData] = useState<AbilityClaim | null>(null)
  const [fetching, setFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastFetched = useRef(data)
  const refetch = useCallback(async () => {
    if (lastFetched.current && !getClaimIsProcessing(lastFetched.current)) {
      return
    }
    setFetching(true)
    try {
      const { data: fetched } = await getClaim({ abilityId: id, testMode })
      setData(fetched ?? null)
    } catch (err: any) {
      console.error(err?.message)
      if (err?.response) console.error(err?.response.data)
      setError(err?.message || 'Error occurred fetching claim')
    }
    setFetching(false)
  }, [id, testMode])
  useEffect(() => {
    refetch()
    const interval = setInterval(
      () => {
        refetch()
      },
      2 * 60 * 1000,
    )
    return () => clearInterval(interval)
  }, [refetch])
  return { data, fetching, error }
}

const ClaimBeingValidated = ({ claim }: { claim: PregnancyClaim }) => (
  <Center w="100%" bg="whiteAlpha.700" borderRadius={4} gap={3} py={2} px={5}>
    <CircularProgress size="20px" isIndeterminate color={colors.green.hex} />
    <VStack spacing={0} align="flex-start">
      <Text fontFamily="Hero-New" color="gray.600" fontSize="sm" fontWeight={500}>
        Claim is being validated by Inovalon
      </Text>
      {claim.validationCheckedOn ? (
        <Text color="gray.500" fontSize="sm">
          VALIDATION CHECKED {getDateTimeString(claim.validationCheckedOn)}
        </Text>
      ) : null}
    </VStack>
  </Center>
)

const LegacyClaimContent = ({ claim }: { claim: PregnancyClaim }) => {
  const {
    serviceType: legacyServiceType,
    serviceTypeOther,
    serviceDates,
    status: claimStatus,
    submittedOn,
    billedCharges,
  } = claim?.legacy ?? {}

  const { value: serviceType } = useFormattedValue(serviceTypeField, legacyServiceType)

  return (
    <>
      <ClaimInfo header="SERVICE TYPE">
        <Text isTruncated>
          {legacyServiceType === 'other' ? `OTHER - ${serviceTypeOther}` : serviceType || 'NONE'}
        </Text>
      </ClaimInfo>
      <ClaimInfo header="STATUS">
        <Text isTruncated>
          {claimStatus ? formatDropdownValue(claimStatus, status.options) : 'None'}
        </Text>
      </ClaimInfo>
      <ClaimInfo header="SERVICE DATES">
        <Text isTruncated>{serviceDates || 'None'}</Text>
      </ClaimInfo>
      <ClaimInfo header="BILLED CHARGES">
        <Text isTruncated>{billedCharges || 'None'}</Text>
      </ClaimInfo>
      <ClaimInfo header="SUBMISSION DATE">
        <Text isTruncated>{submittedOn ? getDateString(submittedOn, 'short') : 'None'}</Text>
      </ClaimInfo>
    </>
  )
}

const LegacyClaimView = ({
  claim,
  onClick,
}: {
  claim: PregnancyClaim
  id: string
  onClick: () => void
}) => (
  <Flex
    onClick={onClick}
    cursor="pointer"
    minH="100%"
    flex={1}
    px={2}
    py={1}
    gap={2}
    // w='100%'
    _hover={{ bg: 'blackAlpha.200' }}>
    {claim.status === 'submitting' ? (
      <ClaimBeingValidated claim={claim} />
    ) : (
      <LegacyClaimContent claim={claim} />
    )}
  </Flex>
)

const AbilityClaimPreview = ({
  abilityId,
  onClick,
  systemClaim,
}: {
  abilityId: string
  systemClaim: WithId<PregnancyClaim>
  onClick: () => void
}) => {
  const { data, fetching, error } = useFetchedClaim(abilityId, version === 'dev')

  return (
    <HStack
      minH="100%"
      onClick={onClick}
      w="100%"
      // bg="gray.100"
      borderRadius={4}
      gap={1}
      p={1}
      cursor="pointer"
      _hover={{ bg: 'whiteAlpha.700' }}
      // border="1px solid #cdcdcd"
    >
      {fetching ? (
        <HStack w="100%" p={2}>
          <Spinner color={colors.green.hex} />
          <Text ml={2} color="gray.500">
            Loading claim from ability
          </Text>
        </HStack>
      ) : (
        <AbilityClaimContent
          preview
          systemClaimId={systemClaim.id}
          systemClaim={systemClaim}
          error={error}
          claim={data}
          abilityValidation={null}
        />
      )}
    </HStack>
  )
}

const RefNumberIcon = () => (
  <Center>
    <Text fontSize="lg" lineHeight={1} fontWeight={600} color="gray.500">
      #
    </Text>
  </Center>
)

export const ClaimView = ({
  claim,
  id,
  onClick,
}: {
  claim: WithId<PregnancyClaim>
  id: string
  onClick: () => void
}) => {
  const body = claim.abilityId ? (
    <AbilityClaimPreview systemClaim={claim} abilityId={claim.abilityId} onClick={onClick} />
  ) : (
    <LegacyClaimView claim={claim} id={id} onClick={onClick} />
  )

  return (
    <HStack spacing={2} w="100%" align="flex-start">
      <Flex
        // bg="white"
        borderRadius={6}
        minW="0"
        overflow="hidden"
        position="relative"
        flex={1}>
        {body}
      </Flex>
      <Flex height="40px" width="25px" position="relative">
        <CopyId bottom="unset" top="0.25rem" left="0" right="unset" item={{ id }} />
        {claim.abilitySnippet?.ClaimNo ? (
          <CopyId
            bottom="unset"
            top="1.8rem"
            left="0"
            right="unset"
            prefix="Ref #:"
            icon={<RefNumberIcon />}
            item={{ id: claim.abilitySnippet.ClaimNo }}
          />
        ) : null}
      </Flex>
    </HStack>
  )
}
